/**
 * First-use tooltip for the command bar.
 *
 * Fires once per user (per browser) to set the framing on day one:
 *
 *   "This is your shortcut. Type what you need and skip the clicks.
 *    Everything also works from the regular menus."
 *
 * Why a tooltip and not a modal: a full-screen onboarding modal is
 * a blocker; a small dismissible tooltip is a nudge. The user can
 * close it and never see it again, OR they can ignore it and it
 * fades away on the first command — either way it didn't get in
 * the way of getting work done.
 *
 * Persistence: ``localStorage.lenavio.commandBar.firstUseSeen``.
 * Per-user is overkill (multiple users on the same browser is
 * rare for HR work) and per-account would require a backend
 * round-trip. localStorage is the right tradeoff.
 */
import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";

const STORAGE_KEY = "lenavio.commandBar.firstUseSeen";

interface CommandBarFirstUseTooltipProps {
  /** When the parent passes ``hidden``, the tooltip never appears.
   *  Used to suppress it on the modal variant (where the user
   *  explicitly opened the command palette and doesn't need
   *  introducing). */
  hidden?: boolean;
}

export function CommandBarFirstUseTooltip({
  hidden,
}: CommandBarFirstUseTooltipProps) {
  // Default to FALSE — we read localStorage in useEffect to keep
  // SSR / hydration safe. The tooltip flicker-in on first render
  // is the price of avoiding hydration mismatches.
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (hidden) return;
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) setShow(true);
    } catch {
      /* localStorage disabled — never show */
    }
  }, [hidden]);

  // Dismiss on first command (the parent re-mounts the bar when
  // ``thread.length`` changes from 0 to 1 — we listen to the same
  // visibility trigger by snapping a "command-in-flight" class on
  // the bar). Simpler approach: just dismiss after 12s if the user
  // didn't close it manually.
  useEffect(() => {
    if (!show) return;
    const t = window.setTimeout(() => {
      setShow(false);
      try {
        window.localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 12_000);
    return () => window.clearTimeout(t);
  }, [show]);

  if (!show) return null;

  const onDismiss = () => {
    setShow(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative mt-2 flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-xs text-text-primary"
    >
      <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" />
      <p className="flex-1 leading-relaxed">
        <strong>This is your shortcut.</strong> Type what you need and
        skip the clicks. Everything also works from the regular menus.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 text-text-muted hover:text-text-primary"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
